import { useCallback, useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";

import eulaHtml from "./eula.html?raw";

export type EulaModalMode = "mandatory" | "readonly";

interface EulaModalProps {
  open: boolean;
  mode: EulaModalMode;
  onAccept?: () => void;
  onDecline?: () => void;
  onClose: () => void;
}

// Full-screen EULA modal. In "mandatory" mode (first login) it cannot be
// dismissed except via Accept/Decline, and Accept stays disabled until the
// user scrolls the document to the bottom. In "readonly" mode (the login
// page's "End User License Agreement" link) it's just a dismissible viewer.
export function EulaModal({ open, mode, onAccept, onDecline, onClose }: Readonly<EulaModalProps>) {
  const isMandatory = mode === "mandatory";
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const checkScrollPosition = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    const fitsWithoutScrolling = el.scrollHeight <= el.clientHeight + 1;
    setScrolledToBottom(atBottom || fitsWithoutScrolling);
  }, []);

  useEffect(() => {
    if (!open) {
      setScrolledToBottom(false);
      return;
    }
    const frame = requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      setScrolledToBottom(false);
      checkScrollPosition();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, mode, checkScrollPosition]);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && !isMandatory && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1500] bg-ink/40 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[1501] flex max-h-[86vh] w-[min(92vw,1200px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-soft border border-rule bg-surface shadow-cardLift"
          onEscapeKeyDown={(e) => isMandatory && e.preventDefault()}
          onInteractOutside={(e) => isMandatory && e.preventDefault()}
          onPointerDownOutside={(e) => isMandatory && e.preventDefault()}
        >
          <div className="shrink-0 bg-litmus-dark-green-800 px-6 py-5">
            <Dialog.Title asChild>
              <h2 className="font-display text-[22px] font-semibold leading-tight text-white">
                End-User License Agreement v. 1.0
              </h2>
            </Dialog.Title>
            <Dialog.Description className="sr-only">
              Litmus Automation Inc. End User License Agreement
            </Dialog.Description>
          </div>

          <div
            ref={scrollRef}
            onScroll={checkScrollPosition}
            data-testid="eula-content-scroll"
            tabIndex={0}
            className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-3"
          >
            {/* Sanitized immediately after conversion from the trusted source document. */}
            {/* eslint-disable-next-line react/no-danger */}
            <div dangerouslySetInnerHTML={{ __html: eulaHtml }} />
          </div>

          <div className="flex shrink-0 flex-wrap justify-end gap-3 border-t border-rule px-6 py-4">
            {isMandatory ? (
              <>
                <button
                  type="button"
                  onClick={onDecline}
                  data-testid="eula-decline-button"
                  className="btn-secondary"
                >
                  Decline
                </button>
                <button
                  type="button"
                  onClick={onAccept}
                  disabled={!scrolledToBottom}
                  data-testid="eula-accept-button"
                  className="btn-primary"
                >
                  Accept
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                data-testid="eula-close-button"
                className="btn-primary"
              >
                Close
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
